import { defineStore } from 'pinia'
import { useRoute } from 'vue-router'

import { useConfig } from '@/composables/config'
import { CUSTOM_PG_EVENTS } from '@/constants/general'
import { useAppStore } from '@/store/app-store'
import { useGamePageStore } from '@/store/game-store'
import { useUserStore } from '@/store/user-store/user-store'
import type {
    AvailableEventGA,
    AvailablePGEvents,
    CustomPGEvents,
    DirectAdEventType,
    EventForGa,
    EventForLoggersType,
    EventGA,
    PlaygamaLoggerEventType,
    PlaygamaSurveyEventTypes,
} from '@/types/logger.types'
import { idleCallbackPromise, timer } from '@/utils/helpers'

const INITIAL_DELAY = 5000 // ms
let initialDelayPromise: null | Promise<void> = null

function initialDelay() {
    if (!initialDelayPromise) {
        initialDelayPromise = timer(INITIAL_DELAY)
    }
    return initialDelayPromise
}

async function sendEvent(url: string, event: PlaygamaLoggerEventType | DirectAdEventType | PlaygamaSurveyEventTypes) {
    await initialDelay()

    try {
        await fetch(url, {
            method: 'post',
            headers: {
                'content-type': 'application/json',
            },
            body: JSON.stringify(event),
            credentials: 'include',
            priority: 'low',
        })
    } catch {
        // 🤷‍♂️
    }
}

async function pushDataLayer(data: Record<string, unknown>) {
    await initialDelay()
    await idleCallbackPromise()
    window.dataLayer.push(data)
}

type EventData = {
    pageName: string | undefined
    gameHru: string | undefined
    clid: string | undefined
    userId: string | undefined
    widgetId: string | undefined
    gameProvider: string | undefined
    gameCategories: string[] | undefined
    experiments: string
}

const checkIsEventGA = (eventName: AvailablePGEvents): eventName is AvailableEventGA =>
    !CUSTOM_PG_EVENTS.includes(eventName as CustomPGEvents)

export const useLoggerStore = defineStore('logger', () => {
    const { ANALYTICS_API_HOST } = useConfig()
    const route = useRoute()

    async function getEventData(): Promise<EventData> {
        const userStore = useUserStore()
        const appStore = useAppStore()
        const gameStore = useGamePageStore()
        const pageName = (route.name as string) || undefined
        const user = await userStore.getUser()

        return {
            clid: appStore.clid,
            userId: user?._id,
            widgetId: appStore.widgetId,
            pageName,
            gameHru: gameStore.game?.hru,
            gameProvider: gameStore.game?.source,
            gameCategories: gameStore.game?.categories,
            experiments: appStore.experimentsString,
        }
    }

    async function logEvent(event: EventForLoggersType): Promise<void> {
        const { clid, userId, widgetId, pageName, gameHru, gameProvider, gameCategories, experiments } =
            await getEventData()

        if (checkIsEventGA(event.eventName)) {
            const gaEvent: EventForGa = {
                event: event.event as EventGA,
                eventName: event.eventName,
                action: event.action || '',
                clid,
                experiments,
                widget_id: widgetId || '',
                game_provider: gameProvider || '',
                game_categories: gameCategories || [],
                ad_placement: event.adPlacement || '',
                ad_unit: event.adUnit || '',
                instream_duration: event.instreamDuration ?? null,
                watched_duration: event.watchedDuration ?? null,
                waterfall_index: event.waterfallIndex ?? null,
                error_message: event.errorMessage || '',
                amount_in_game_currency: event.amountInGameCurrency ?? null,
            }
            pushDataLayer(gaEvent)
        }

        if (event.event === 'direct_ad_event') {
            const directAdEvent: DirectAdEventType = {
                eventName: event.eventName,
                clid,
                widgetId,
                userId,
                pageName,
                gameHru,
                adUnit: event.adUnit || '',
                instreamDuration: event.instreamDuration,
                watchedDuration: event.watchedDuration,
            }
            await sendEvent(`${ANALYTICS_API_HOST}/api/v1/events/direct-ads`, directAdEvent)
            return
        }

        const analyticsEvent: PlaygamaLoggerEventType = {
            eventName: event.eventName,
            pageName,
            gameHru,
            userId,
            clid,
            widgetId,
        }
        await sendEvent(`${ANALYTICS_API_HOST}/api/v1/events`, analyticsEvent)
    }

    async function logInterfaceEvents(props: {
        interface_target?: string
        interface_path?: string
        eventName: AvailableEventGA
    }) {
        const { clid, widgetId } = await getEventData()
        const event: EventForGa = {
            event: 'interface_event',
            route_name: route.name as string,
            clid,
            widget_id: widgetId || '',
            interface_path: props.interface_path || 'unknown',
            interface_target: props.interface_target || 'unknown',
            eventName: props.eventName,
        }
        pushDataLayer(event)
    }

    async function logSearchEvent(props: { search_term: string; search_result_count: number }) {
        const { clid, widgetId } = await getEventData()
        const event: EventForGa = {
            event: 'view_search_results',
            eventName: 'view_search_results',
            clid,
            widget_id: widgetId || '',
            search_term: props.search_term,
            search_result_count: props.search_result_count,
        }
        window.dataLayer.push(event)
    }

    async function logSurveyEvent(event: PlaygamaSurveyEventTypes): Promise<void> {
        await sendEvent(`${ANALYTICS_API_HOST}/api/v1/events/survey`, event)
    }

    return {
        logEvent,
        logInterfaceEvents,
        logSurveyEvent,
        logSearchEvent,
    }
})
